import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, tCount } from '../../i18n/uiText';
import { jsonHeadersWithAuth } from '../../utils/webAuth';
import { requestAuthPopup } from '../../utils/authPopup';
import { resolveAiLimitMessage } from '../../utils/aiLimitMessage';
import { bibleService } from '../../services/bible';
import { parseVerseReferenceFromSearchQuery } from '../../utils/parseVerseReferenceQuery';
import { telegramService } from '../../services/telegram';
import {
  clearContextSearchSession,
  loadContextSearchSession,
  saveContextSearchSession,
} from '../../utils/contextSearchSession';

function isTgMobileFullscreen() {
  return telegramService.isTelegramWebApp && !telegramService.isTelegramWideLayout;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';
const normalizeSearch = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();

const parseSearchQuery = (query) => {
  const q = normalizeSearch(query);
  if (!q) return { raw: '', bookPart: '', chapterToken: '' };
  const chapterMatch = q.match(/(?:^|\s)(\d{1,3})$/);
  const chapterToken = chapterMatch ? chapterMatch[1] : '';
  const bookPart = chapterMatch ? q.slice(0, chapterMatch.index).trim() : q;
  return { raw: q, bookPart, chapterToken };
};

const hasMatchingChapter = (maxChapter, chapterToken) => {
  if (!chapterToken) return true;
  for (let n = 1; n <= maxChapter; n += 1) {
    if (String(n).includes(chapterToken)) return true;
  }
  return false;
};

const readMaxVerseFromBook = (book, chapter) => {
  if (!book || !Number.isFinite(chapter) || chapter < 1) return null;
  const counts = Array.isArray(book.chapterVerseCounts) ? book.chapterVerseCounts : null;
  if (!counts) return null;
  const value = Number(counts[chapter - 1]);
  if (!Number.isFinite(value) || value < 1) return null;
  return Math.floor(value);
};

export default function ContextSearchModal({ translation = 'kjv', onClose }) {
  const MAX_CONTEXT_RESULTS = 10;
  const uiLang = (localStorage.getItem('bibleLanguage') || 'en').toLowerCase();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const chapterMetaCacheRef = useRef(new Map());
  const savedSession = useMemo(() => loadContextSearchSession(translation), [translation]);
  const [query, setQuery] = useState(savedSession?.query ?? '');
  const [results, setResults] = useState(savedSession?.results ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(savedSession?.error ?? null);
  const [hasSearched, setHasSearched] = useState(savedSession?.hasSearched ?? false);
  const [bookNamesById, setBookNamesById] = useState({});
  const [booksList, setBooksList] = useState([]);
  const [expandedBooks, setExpandedBooks] = useState(savedSession?.expandedBooks ?? {});
  const [tgMobileFull, setTgMobileFull] = useState(isTgMobileFullscreen);
  /** Распознанная ссылка на место Писания по текущей строке (без отправки ИИ-поиска). */
  const [liveDirectRef, setLiveDirectRef] = useState(null);
  const parsedSearch = useMemo(() => parseSearchQuery(query), [query]);
  const preSubmitBooks = useMemo(() => {
    if (!parsedSearch.raw || !Array.isArray(booksList) || booksList.length === 0) return [];
    return booksList.filter((book) => {
      const countNum = Number(book?.chapters ?? 0);
      const chapterCount = Number.isFinite(countNum) && countNum > 0 ? Math.floor(countNum) : 0;
      if (!hasMatchingChapter(chapterCount, parsedSearch.chapterToken)) return false;
      if (!parsedSearch.bookPart) return true;
      const haystack = normalizeSearch(`${book?.name ?? ''} ${book?.nameShort ?? ''} ${book?.id ?? ''}`);
      const compactHaystack = haystack.replace(/\s+/g, '');
      const compactNeedle = parsedSearch.bookPart.replace(/\s+/g, '');
      return haystack.includes(parsedSearch.bookPart) || compactHaystack.includes(compactNeedle);
    });
  }, [parsedSearch, booksList]);

  const goToDirectRef = useCallback(
    (directRef) => {
      if (!directRef || !Number.isFinite(directRef.bookId) || !Number.isFinite(directRef.chapter)) return;
      const tr = directRef.translation || translation;
      const url =
        directRef.verse != null
          ? `/${tr}/${directRef.bookId}/${directRef.chapter}/${directRef.verse}`
          : `/${tr}/${directRef.bookId}/${directRef.chapter}`;
      navigate(url);
      onClose?.();
    },
    [navigate, onClose, translation]
  );

  const tryParseDirectRef = useCallback(
    async (rawQ) => {
      const trimmed = String(rawQ ?? '').trim();
      if (!trimmed) return null;
      let books = booksList;
      if (!books.length) {
        books = await bibleService.getBooks(translation);
      }
      if (!Array.isArray(books) || !books.length) return null;
      const directRef = parseVerseReferenceFromSearchQuery(trimmed, books, translation);
      if (!directRef || !Number.isFinite(directRef.bookId) || !Number.isFinite(directRef.chapter)) {
        return null;
      }

      const normalizedChapter = Math.floor(Number(directRef.chapter));
      if (!Number.isFinite(normalizedChapter) || normalizedChapter < 1) return null;

      const book = books.find((item) => Number(item?.id) === Number(directRef.bookId));
      const bookChapters = Math.floor(Number(book?.chapters));
      if (Number.isFinite(bookChapters) && bookChapters > 0 && normalizedChapter > bookChapters) {
        return null;
      }

      if (directRef.verse == null) {
        return { ...directRef, chapter: normalizedChapter };
      }

      const normalizedVerse = Math.floor(Number(directRef.verse));
      if (!Number.isFinite(normalizedVerse) || normalizedVerse < 1) return null;

      const tr = directRef.translation || translation;
      const cacheKey = `${tr}-${directRef.bookId}-${normalizedChapter}`;
      let maxVerse = chapterMetaCacheRef.current.get(cacheKey);

      if (!Number.isFinite(maxVerse)) {
        maxVerse = readMaxVerseFromBook(book, normalizedChapter);
      }

      if (!Number.isFinite(maxVerse)) {
        const chapterData = await bibleService.getChapter(directRef.bookId, normalizedChapter, tr);
        const verses = Array.isArray(chapterData?.verses) ? chapterData.verses : [];
        maxVerse = verses.reduce((max, item) => {
          const verseNum = Number(item?.verse);
          return Number.isFinite(verseNum) && verseNum > max ? verseNum : max;
        }, 0);
      }

      if (!Number.isFinite(maxVerse) || maxVerse < 1 || normalizedVerse > maxVerse) {
        return null;
      }

      chapterMetaCacheRef.current.set(cacheKey, maxVerse);
      return { ...directRef, chapter: normalizedChapter, verse: normalizedVerse };
    },
    [booksList, translation]
  );

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setLiveDirectRef(null);
      return;
    }
    let cancelled = false;
    const id = window.setTimeout(async () => {
      const ref = await tryParseDirectRef(q);
      if (cancelled) return;
      setLiveDirectRef(ref);
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [query, tryParseDirectRef]);

  useEffect(() => {
    const syncLayout = () => setTgMobileFull(isTgMobileFullscreen());
    syncLayout();
    const root = document.documentElement;
    const mo = new MutationObserver(syncLayout);
    mo.observe(root, {
      attributes: true,
      attributeFilter: ['data-tg-desktop', 'data-tg-webapp', 'data-tg-fullscreen'],
    });
    window.addEventListener('resize', syncLayout);
    return () => {
      mo.disconnect();
      window.removeEventListener('resize', syncLayout);
    };
  }, []);

  useEffect(() => {
    saveContextSearchSession(translation, {
      query,
      results,
      hasSearched,
      error,
      expandedBooks,
    });
  }, [translation, query, results, hasSearched, error, expandedBooks]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const focusInput = () => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      const pos = inputRef.current.value.length;
      inputRef.current.setSelectionRange?.(pos, pos);
    };
    const tId = requestAnimationFrame(focusInput);
    const tId2 = window.setTimeout(focusInput, 120);
    return () => {
      cancelAnimationFrame(tId);
      window.clearTimeout(tId2);
      document.body.style.overflow = '';
    };
  }, []);

  // На мобильных держим модалку поиска привязанной к верху и подстраиваем под visualViewport.
  useEffect(() => {
    const node = panelRef.current;
    if (!node || typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;

    const apply = () => {
      if (tgMobileFull) {
        node.style.maxHeight = '';
        return;
      }
      const cap = Math.max(220, Math.round(vv.height - 16));
      node.style.maxHeight = `${cap}px`;
    };

    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      node.style.maxHeight = '';
    };
  }, [tgMobileFull]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setHasSearched(false);
    setError(null);
    setLiveDirectRef(null);
    setExpandedBooks({});
    clearContextSearchSession();
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const books = await bibleService.getBooks(translation);
      if (cancelled) return;
      const names = {};
      for (const book of books || []) {
        if (book?.id != null && typeof book?.name === 'string') {
          names[Number(book.id)] = book.name;
        }
      }
      setBookNamesById(names);
      setBooksList(Array.isArray(books) ? books : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [translation]);

  const runSearch = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    setHasSearched(true);
    requestAnimationFrame(() => inputRef.current?.focus());

    const directRef = await tryParseDirectRef(q);
    if (directRef) {
      setLoading(false);
      setError(null);
      setResults([]);
      goToDirectRef(directRef);
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const language = localStorage.getItem('bibleLanguage') || 'en';
      const response = await fetch(`${API_BASE}/search/context/stream`, {
        method: 'POST',
        headers: jsonHeadersWithAuth({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ query: q, language, maxResults: MAX_CONTEXT_RESULTS }),
      });

      if (response.status === 401) {
        const next = `${window.location.pathname}${window.location.search}`;
        requestAuthPopup(next, true);
        throw new Error('__AUTH_REQUIRED__');
      }
      if (response.status === 429) {
        const payload = await response.json().catch(() => null);
        if (payload?.code === 'DAILY_AI_LIMIT_REACHED') {
          throw new Error(resolveAiLimitMessage(payload, t, 'context_search_stream'));
        }
      }
      if (!response.ok) throw new Error(t('error'));
      if (!response.body) throw new Error(t('error'));

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let chunkBuffer = '';
      let eventName = '';
      const dedupe = new Set();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunkBuffer += decoder.decode(value, { stream: true });
        const lines = chunkBuffer.split('\n');
        chunkBuffer = lines.pop() || '';

        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line) continue;
          if (line.startsWith('event:')) {
            eventName = line.slice('event:'.length).trim();
            continue;
          }
          if (!line.startsWith('data:')) continue;
          const payload = line.slice('data:'.length).trim();

          if (eventName === 'result') {
            let item = null;
            try {
              item = JSON.parse(payload);
            } catch {
              continue;
            }
            if (!item) continue;
            const normalized = {
              ...item,
              reason:
                typeof item.reason === 'string'
                  ? item.reason.trim().replace(/\s+/g, ' ').slice(0, 120)
                  : '',
            };
            const key = `${item.bookId}.${item.chapter}.${item.verse || 0}`;
            if (dedupe.has(key)) continue;
            dedupe.add(key);
            setResults((prev) => [...prev, normalized].slice(0, MAX_CONTEXT_RESULTS));
          }

          if (eventName === 'done') {
            break;
          }
        }
      }
    } catch (err) {
      if (err?.message === '__AUTH_REQUIRED__') return;
      setError(err.message || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const goToResult = (item) => {
    const url = item.verse
      ? `/${translation}/${item.bookId}/${item.chapter}/${item.verse}`
      : `/${translation}/${item.bookId}/${item.chapter}`;
    navigate(url);
    onClose?.();
  };

  const formatReference = (item) => {
    const bookName = bookNamesById[Number(item.bookId)];
    const fallback = `${item.bookId}:${item.chapter}${item.verse ? `:${item.verse}` : ''}`;
    if (!bookName) return item.reference || fallback;
    if (item.verse != null) return `${bookName} ${item.chapter}:${item.verse}`;
    return `${bookName} ${item.chapter}`;
  };

  const groupedResults = useMemo(() => {
    if (!results.length) return [];
    const order = [];
    const byBook = new Map();
    for (const item of results) {
      const key = Number(item.bookId) || 0;
      if (!byBook.has(key)) {
        byBook.set(key, []);
        order.push(key);
      }
      byBook.get(key).push(item);
    }
    return order.map((bookId) => ({
      bookId,
      bookName: bookNamesById[bookId] || '',
      items: byBook.get(bookId) || [],
    }));
  }, [results, bookNamesById]);

  useEffect(() => {
    if (!groupedResults.length) {
      setExpandedBooks({});
      return;
    }
    setExpandedBooks((prev) => {
      const next = {};
      groupedResults.forEach((group) => {
        const key = String(group.bookId);
        // По умолчанию все группы свернуты.
        next[key] = prev[key] ?? false;
      });
      return next;
    });
  }, [groupedResults]);

  const toggleBookGroup = (bookId) => {
    const key = String(bookId);
    setExpandedBooks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getGroupCountLabel = (bookLabel, count) => {
    const pr = new Intl.PluralRules(uiLang);
    const rule = pr.select(count);
    const key = `contextSearchGroupCount_${rule}`;
    const fallbackKey = rule === 'one' ? 'contextSearchGroupCount_one' : 'contextSearchGroupCount_other';
    const template = t(key) !== key ? t(key) : t(fallbackKey);
    return template
      .replace('{book}', bookLabel)
      .replace('{count}', String(count));
  };

  const formatGroupHeader = (group) => {
    const bookLabel = group.bookName || `${t('bookWord')} ${group.bookId}`;
    if (group.items.length === 1) {
      const template = t('contextSearchGroupSingle');
      return (template !== 'contextSearchGroupSingle' ? template : '{book} — 1 verse: {reference}')
        .replace('{book}', bookLabel)
        .replace('{reference}', formatReference(group.items[0]))
        .replace('{count}', '1');
    }
    return getGroupCountLabel(bookLabel, group.items.length);
  };

  const canClearSearch =
    Boolean(query.trim()) || hasSearched || results.length > 0 || Boolean(error);

  return (
    <div
      className={[
        'context-search-root',
        tgMobileFull ? 'context-search-root--tg-full' : '',
      ].join(' ')}
      role="dialog"
      aria-modal="true"
      aria-label={t('search')}
    >
      <button
        type="button"
        className="context-search-dismiss"
        aria-label={t('close')}
        onClick={onClose}
      />
      <div ref={panelRef} className="context-search-panel" onClick={(e) => e.stopPropagation()}>
        <div className="context-search-panel-top">
          <div className="context-search-handle" aria-hidden="true" />
          <header className="context-search-head">
            <div className="context-search-title-row">
              <span className="context-search-title-emoji" aria-hidden="true">
                🔍
              </span>
              <h2 className="context-search-title">{t('search')}</h2>
            </div>
            <button
              type="button"
              className="context-search-close"
              onClick={onClose}
              aria-label={t('close')}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </header>
          <p className="context-search-sub">{t('contextSearchHint')}</p>

          <form onSubmit={runSearch} className="context-search-form">
            <div className="context-search-field">
              <span className="context-search-field-icon" aria-hidden="true">
                🔎
              </span>
              <input
                ref={inputRef}
                type="search"
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onPaste={() => {
                  window.setTimeout(async () => {
                    const v = inputRef.current?.value;
                    const ref = await tryParseDirectRef(v);
                    setLiveDirectRef(ref);
                  }, 0);
                }}
                placeholder={t('contextSearchPlaceholder')}
                className="context-search-input"
              />
              {canClearSearch ? (
                <button
                  type="button"
                  className="context-search-clear"
                  onClick={clearSearch}
                  aria-label={t('contextSearchClear')}
                >
                  <span aria-hidden="true">✕</span>
                </button>
              ) : null}
              <button
                type="submit"
                className="context-search-submit"
                disabled={loading || !query.trim()}
                aria-label={t('search')}
              >
                <span aria-hidden="true">{loading ? '⏳' : '➤'}</span>
              </button>
            </div>
          </form>
        </div>

        <div className="context-search-panel-scroll">
          {liveDirectRef && !loading && (
            <div className="context-search-direct-hit-wrap">
              <button
                type="button"
                className="context-search-row context-search-direct-hit"
                onClick={() => goToDirectRef(liveDirectRef)}
              >
                <span className="context-search-row-icon" aria-hidden="true">
                  📖
                </span>
                <span className="context-search-row-body">
                  <span className="context-search-row-ref">{formatReference(liveDirectRef)}</span>
                  <span className="context-search-row-reason">{t('contextSearchGoToRef')}</span>
                </span>
                <span className="context-search-row-chevron" aria-hidden="true">
                  ›
                </span>
              </button>
            </div>
          )}

          {!hasSearched && query.trim() && preSubmitBooks.length > 0 && (
            <div className="context-search-groups">
              {preSubmitBooks.slice(0, 8).map((book) => (
                <section key={`pre-book-${book.id}`} className="context-search-group">
                  <button
                    type="button"
                    className="context-search-group-toggle"
                    onClick={() => navigate(`/${translation}/${book.id}`)}
                  >
                    <span className="context-search-group-title">{book.name}</span>
                    <span className="context-search-row-reason">
                      {tCount('chapterCountCompact', book.chapters || 0)}
                    </span>
                  </button>
                </section>
              ))}
            </div>
          )}

          <div className="context-search-results-wrap">
          {error && <p className="context-search-error">{error}</p>}

          {loading && (
            <div className="context-search-loading" aria-live="polite">
              <span className="context-search-loading-dot" />
              <span className="context-search-loading-dot" />
              <span className="context-search-loading-dot" />
              <span className="context-search-loading-text">{t('contextSearchSearching')}</span>
            </div>
          )}

          {!loading && !error && hasSearched && results.length === 0 && (
            <p className="context-search-empty">{t('noData')}</p>
          )}

          {groupedResults.length > 0 && (
            <div className="context-search-groups">
              {groupedResults.map((group) => (
                <section key={`book-${group.bookId}`} className="context-search-group">
                  <button
                    type="button"
                    className="context-search-group-toggle"
                    onClick={() => toggleBookGroup(group.bookId)}
                    aria-expanded={Boolean(expandedBooks[String(group.bookId)])}
                  >
                    <span className="context-search-group-title">
                      {formatGroupHeader(group)}
                    </span>
                    <span
                      className={`context-search-group-caret${expandedBooks[String(group.bookId)] ? ' is-open' : ''}`}
                      aria-hidden="true"
                    >
                      ▾
                    </span>
                  </button>
                  {expandedBooks[String(group.bookId)] && (
                    <ul className="context-search-results" role="listbox">
                      {group.items.map((item, idx) => (
                        <li key={`${item.bookId}-${item.chapter}-${item.verse ?? 'c'}-${idx}`} role="none">
                          <button
                            type="button"
                            role="option"
                            className="context-search-row"
                            onClick={() => goToResult(item)}
                          >
                            <span className="context-search-row-icon" aria-hidden="true">
                              📖
                            </span>
                            <span className="context-search-row-body">
                              <span className="context-search-row-ref">
                                {formatReference(item)}
                              </span>
                              {item.reason ? (
                                <span className="context-search-row-reason">{item.reason}</span>
                              ) : null}
                            </span>
                            <span className="context-search-row-chevron" aria-hidden="true">
                              ›
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
